import { PolymerElement, html } from '@polymer/polymer';
import { mixinBehaviors } from '@polymer/polymer/lib/legacy/class';
import { KatapultEditorTemplate } from './katapult-editor-template';
import { DefaultComputedBindings } from '../../modules/DefaultComputedBindings';

import '../icon-picker-dialog/icon-picker-dialog';
import '../katapult-color-picker/katapult-color-picker-dialog';
import '../katapult-elements/katapult-button.js';
import '../katapult-elements/katapult-icon.js';
import { CamelCase } from '../../modules/CamelCase.js';

import { LazySet } from '../../modules/LazySet.js';
import { Path } from '../../modules/Path.js';
import { ToArray } from '../../modules/ToArray.js';
import { addFlagListener, removeFlagListener } from '../../modules/FeatureFlags.js';

/* global k, firebase, customElements */

class KatapultAssessmentEditor extends mixinBehaviors([DefaultComputedBindings], KatapultEditorTemplate) {
  static get template() {
    return html`
      ${super.style}
      <style include="paper-table-style">
        [hidden] {
          display: none !important;
        }
        .body {
          margin-left: 48px;
        }
        paper-tabs {
          --paper-tabs-selection-bar-color: var(--secondary-color);
          --paper-tab-ink: var(--secondary-color);
        }
        paper-tab {
          --paper-tab: {
            font-size: 12pt;
            font-weight: normal;
          };
          --paper-tab-content-focused: {
            font-weight: normal;
          };
        }

        paper-header-content {
          background-color: white;
          color: var(--paper-grey-800);
        }
        paper-header-content paper-input {
          text-transform: none;
        }

        [drag-handle]:hover {
          cursor: move;
        }

        .content-row {
          margin-bottom: 16px;
          display: flex;
        }

        .content-row-icon {
          padding: 8px;
          margin-right: 16px;
        }

        .content-row-item {
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .content-row-label {
          margin-top: 8px;
          margin-bottom: 16px;
          font-size: 16px;
        }

        .button-add-image {
          background-color: var(--paper-grey-200);
          width: fit-content;
        }
      </style>

      <katapult-firebase-worker path="[[modelSpaceDetails.path]]/models/attributes" data="{{otherAttributes}}"></katapult-firebase-worker>
      <katapult-firebase-worker path="[[modelSpaceDetails.path]]/models/input_models" data="{{inputModels}}"></katapult-firebase-worker>
      <katapult-firebase-worker path="[[modelSpaceDetails.path]]/models/button_groups" data="{{toolsets}}"></katapult-firebase-worker>
      <katapult-firebase-worker path="[[modelSpaceDetails.path]]/models/mapping_buttons" data="{{tools}}"></katapult-firebase-worker>

      <icon-picker-dialog
        id="iconPickerDialog"
        model-key="[[modelSpaceDetails.$key]]"
        on-icon-selected="iconSelected"
        fit-into="[[$this]]"
      ></icon-picker-dialog>
      <katapult-color-picker-dialog id="colorPickerDialog" on-save="colorSelected" fit-into="[[$this]]"></katapult-color-picker-dialog>

      <template is="dom-repeat" items="[[steps]]" as="step">
        <katapult-editor-step
          creating-item="[[creatingItem]]"
          active="[[step.active]]"
          index="[[step.index]]"
          current-index="[[currentIndex]]"
          title="[[step.title]]"
        >
          <template is="dom-if" if="[[equal(step.id, 'assessment_style')]]" restamp>
            <katapult-button on-click="chooseIcon" slot="body" style="margin-right: 8px;"
              ><iron-icon style$="color: [[_itemData.color]]; margin-right: 5px;" icon="[[_itemData.icon]]"></iron-icon
              ><span>Icon</span></katapult-button
            >
            <katapult-button on-click="chooseColor" slot="body" style="margin-right: 8px;"
              ><iron-icon style$="color: [[_itemData.color]]; margin-right: 5px;" icon="image:palette"></iron-icon
              ><span>Color</span></katapult-button
            >
            <katapult-button on-click="nextStep" slot="current-step-body">CONTINUE</katapult-button>
          </template>

          <template is="dom-if" if="[[equal(step.id, 'assessment_permission')]]" restamp>
            <katapult-button on-click="nextStep" slot="current-step-header">Continue</katapult-button>
            <paper-radio-group
              slot="body"
              selected="[[firstValid(_itemData.assessment_permission, 'default')]]"
              style="display: flex; flex-direction: column;"
              on-selected-changed="assessmentPermissionChanged"
            >
              <paper-radio-button name="default"
                >Assess Existing and Draw New Nodes
                <span style="color: var(--primary-text-color-faded);">(default)</span></paper-radio-button
              >
              <paper-radio-button name="existing_only">Assess Existing Nodes</paper-radio-button>
              <paper-radio-button name="existing_sections_only">Assess Existing Sections</paper-radio-button>
              <paper-radio-button name="existing_nodes_and_sections">Assess Existing Nodes and Sections</paper-radio-button>
              <paper-radio-button name="new_only">Draw New Nodes</paper-radio-button>
            </paper-radio-group>
          </template>

          <template is="dom-if" if="[[equal(step.id, 'assessment_steps')]]" restamp>
            <katapult-button on-click="nextStep" slot="current-step-header">Continue</katapult-button>
            <div slot="body" style="width: 100%; display: flex; flex-direction: column; gap: 8px;">
              <paper-table style="overflow: hidden; flex-grow: 1;" rounded white outline>
                <paper-row raised-on-scroll grey>
                  <paper-cell icon><iron-icon icon="icons:swap-vert"></iron-icon></paper-cell>
                  <paper-cell icon></paper-cell>
                  <paper-cell>Step Name</paper-cell>
                  <paper-cell>Step Type</paper-cell>
                  <paper-cell>Details</paper-cell>
                  <paper-cell></paper-cell>
                  <paper-cell style="max-width: 85px;">Required</paper-cell>
                  <paper-cell icon-button on-click="toggleExpandAll"
                    ><katapult-button iconOnly noBorder noBackground icon="unfold_more"></katapult-button
                  ></paper-cell>
                  <paper-cell icon></paper-cell>
                </paper-row>

                <paper-table-scroll on-sort-changed="stepsArraySorted">
                  <template is="dom-if" if="[[!numAssessmentSteps]]">
                    <paper-row slot="before">
                      <paper-cell center style="color: var(--paper-grey-600);">
                        <iron-icon icon="katapult-misc:search-off"></iron-icon>
                        <span>No Steps Yet</span>
                      </paper-cell>
                    </paper-row>
                  </template>
                  <template
                    is="dom-repeat"
                    items="{{stepsArray}}"
                    index-as="stepIndex"
                    filter="isStep"
                    rendered-item-count="{{numAssessmentSteps}}"
                  >
                    <paper-header-content
                      white
                      opened="{{item.$opened}}"
                      no-click-row
                      slot="sortable"
                      divider
                      no-animation="[[disablePicklistAnimation]]"
                    >
                      <!--HEADER ROW-->
                      <paper-cell drag-handle icon slot="header">
                        <span>[[item.order]]</span>
                      </paper-cell>
                      <paper-cell icon slot="header">
                        <katapult-icon icon="[[getStepIcon(item, item.type, item.$gui_element)]]"></katapult-icon>
                      </paper-cell>
                      <paper-cell slot="header">
                        <paper-input label="Step Title" value="{{item.title}}" no-label-float></paper-input>
                      </paper-cell>
                      <paper-cell slot="header">
                        <katapult-drop-down
                          items="[[stepTypes]]"
                          label-path="$val"
                          value-path="$key"
                          label="Select a Type"
                          value="{{item.type}}"
                          no-label-float
                          on-selected-will-change="stepTypeChanged"
                        ></katapult-drop-down>
                      </paper-cell>

                      <!--HEADER ROW DETAILS-->
                      <template is="dom-if" if="[[hasHeaderRowDetails(item.type, 'attribute')]]" restamp>
                        <paper-cell slot="header">
                          <katapult-drop-down
                            label="Select an Attribute"
                            items="[[attributeKeysFilteredByType]]"
                            label-function="[[camelCase]]"
                            value="{{item.attribute}}"
                            no-label-float
                            on-selected-will-change="stepAttributeChanged"
                          >
                            <paper-item on-click="createNewAttribute">CREATE NEW ATTRIBUTE</paper-item>
                          </katapult-drop-down>
                        </paper-cell>
                        <paper-cell slot="header">
                          <template is="dom-if" if="[[isDropdown(item.$gui_element)]]">
                            <katapult-drop-down
                              label="Select a Picklist"
                              items="[[getAttributePicklists(item.attribute)]]"
                              label-function="[[camelCase]]"
                              value="{{item.picklist}}"
                              hidden="[[hidePicklistSelect(item, item.attribute)]]"
                              no-label-float
                              on-selected-will-change="stepPicklistChanged"
                            ></katapult-drop-down>
                          </template>
                        </paper-cell>
                      </template>
                      <template is="dom-if" if="[[hasHeaderRowDetails(item.type, 'photo')]]" restamp>
                        <paper-cell slot="header">
                          <katapult-drop-down
                            label="Select a Classification"
                            items="[[getChipAttributes(item.attribute)]]"
                            label-function="[[boundChipAttributeLabelFunction]]"
                            value="{{item.classification}}"
                            no-label-float
                            on-selected-will-change="stepClassificationChanged"
                          ></katapult-drop-down>
                        </paper-cell>
                        <paper-cell slot="header"></paper-cell>
                      </template>
                      <template is="dom-if" if="[[hasHeaderRowDetails(item.type, 'drawing_tool')]]" restamp>
                        <paper-cell slot="header">
                          <katapult-drop-down
                            label="Select a Drawing Tool"
                            label-path="label"
                            value-path="value"
                            items="[[drawingTools]]"
                            value="{{item.drawing_tool}}"
                            no-label-float
                          ></katapult-drop-down>
                        </paper-cell>
                        <paper-cell slot="header"></paper-cell>
                      </template>
                      <template is="dom-if" if="[[hasHeaderRowDetails(item.type, '$else')]]" restamp>
                        <paper-cell slot="header"></paper-cell>
                        <paper-cell slot="header"></paper-cell>
                      </template>

                      <paper-cell style="max-width: 85px; justify-content: center;" slot="header">
                        <template is="dom-if" if="[[equal(item.type, 'photo_group')]]">
                          <template is="dom-if" if="[[item.$some_required]]">
                            <iron-icon
                              icon="icons:indeterminate-check-box"
                              style="color: var(--paper-red-500); cursor: pointer;"
                              on-click="requireAllPhotoPrompts"
                            ></iron-icon>
                          </template>
                          <template is="dom-if" if="[[!item.$some_required]]">
                            <paper-checkbox
                              style="--primary-color: var(--paper-red-500);"
                              no-label
                              checked="[[isPhotoGroupRequired(item, item.photo_prompts.*)]]"
                              on-checked-changed="togglePhotoGroupRequired"
                            ></paper-checkbox>
                          </template>
                        </template>
                        <template is="dom-if" if="[[!equal(item.type, 'photo_group')]]">
                          <paper-checkbox
                            style="--primary-color: var(--paper-red-500);"
                            no-label
                            checked="{{item.required}}"
                          ></paper-checkbox>
                        </template>
                      </paper-cell>
                      <paper-cell icon-button slot="header-end" on-click="promptDeleteAssessmentStep">
                        <katapult-button iconOnly noBorder icon="delete"></katapult-button>
                      </paper-cell>

                      <!--CONTENT ROW-->
                      <div style="background: var(--paper-grey-50); padding: 24px;" slot="content">
                        <!--All: Prompt Text-->
                        <div class="content-row">
                          <iron-icon class="content-row-icon" icon="communication:chat"></iron-icon>
                          <paper-input label="Prompt Text" value="{{item.prompt}}" no-label-float></paper-input>
                        </div>
                        <!--All: Prompt Image-->
                        <div class="content-row">
                          <iron-icon class="content-row-icon" icon="image:photo"></iron-icon>
                          <div class="content-row-item">
                            <div class="content-row-label">Image for Prompt</div>
                            <template is="dom-if" if="[[!item.$upload]]" restamp>
                              <template is="dom-if" if="[[!item.image]]">
                                <katapult-button class="button-add-image" on-click="openImageSelect">Browse</katapult-button>
                              </template>
                              <template is="dom-if" if="[[item.image]]">
                                <!--FEAT: Add image preview-->
                                <div style="font-size: 16px; display: flex">
                                  <span style="margin-right: 16px; align-self: center"
                                    >[[item.image.name]] ([[getImageSize(item.image.size)]] MB)</span
                                  >
                                  <katapult-button iconOnly noBorder icon="delete" on-click="deleteImage"></katapult-button>
                                </div>
                              </template>
                            </template>
                            <template is="dom-if" if="[[item.$upload]]" restamp>
                              <katapult-button
                                style="background: linear-gradient(90deg, var(--paper-grey-300) [[item.$upload.progress]]%, var(--paper-grey-200) [[item.$upload.progress]]%)"
                                class="button-add-image"
                                >[[item.$upload.status]]</katapult-button
                              >
                            </template>
                          </div>
                        </div>

                        <!-- Set Location Options -->
                        <template is="dom-if" if="[[equal(item.type, 'set_location')]]" restamp>
                          <template is="dom-if" if="[[item.required]]">
                            <div class="content-row">
                              <iron-icon class="content-row-icon" icon="icons:check"></iron-icon>
                              <div class="content-row-item">
                                <div class="content-row-label">Location Verification</div>
                                <katapult-drop-down
                                  no-label-float
                                  label="GPS Verification Attribute"
                                  items="[[getAttributeKeys(otherAttributes, 'node', 'checkbox')]]"
                                  value="{{item.gps_verification_attribute}}"
                                ></katapult-drop-down>
                                <paper-checkbox checked="{{item.require_gps}}">Require verification to be done with GPS</paper-checkbox>
                              </div>
                            </div>
                          </template>
                        </template>

                        <!-- General attribute configuration options -->
                        <template is="dom-if" if="[[equal(item.type, 'attribute')]]" restamp>
                          <div class="content-row">
                            <iron-icon class="content-row-icon" icon="icons:list"></iron-icon>
                            <div class="content-row-item">
                              <div class="content-row-label">Additional Attribute Options</div>
                              <paper-checkbox checked="{{item.show_use_last_entered_option}}"
                                >Allow "Use Last Entered Value" Option</paper-checkbox
                              >
                              <template is="dom-if" if="[[isDropdown(item.$gui_element)]]">
                                <paper-checkbox style="margin-top: 10px" checked="{{item.display_dropdown_as_dropdown}}"
                                  >Display Dropdown attribute as a "Dropdown"</paper-checkbox
                                >
                              </template>
                            </div>
                          </div>
                        </template>

                        <!-- Set Address Data -->
                        <template is="dom-if" if="[[equal(item.type, 'set_address_data')]]" restamp>
                          <div class="content-row">
                            <iron-icon class="content-row-icon" icon="maps:place"></iron-icon>
                            <div class="content-row-item">
                              <div class="content-row-label">Address Components</div>
                              <template is="dom-repeat" items="[[addressComponents]]" as="addressComponent">
                                <katapult-drop-down
                                  items="[[getAttributeKeys(otherAttributes, 'node')]]"
                                  step-index="[[stepIndex]]"
                                  value="[[getAddressComponentAttribute(item.uid, addressComponent)]]"
                                  on-selected-changed="addressComponentAttributeChanged"
                                  label="{{addressComponent.title}} Attribute"
                                ></katapult-drop-down>
                              </template>
                            </div>
                          </div>
                          <!-- Options -->
                          <div class="content-row">
                            <iron-icon class="content-row-icon" icon="settings"></iron-icon>
                            <div class="content-row-item">
                              <div class="content-row-label">Options</div>
                              <template is="dom-repeat" items="[[stepOptions]]" as="option">
                                <template is="dom-if" if="[[equal(option.step_type, 'set_address_data')]]">
                                  <paper-checkbox
                                    checked="[[getAddressComponentOption(item.uid, option)]]"
                                    step-index="[[stepIndex]]"
                                    on-checked-changed="addressComponentOptionChanged"
                                    >{{option.title}}</paper-checkbox
                                  >
                                </template>
                              </template>
                            </div>
                          </div>
                        </template>

                        <!--Photo Configuration Options-->
                        <template is="dom-if" if="[[equal(item.type, 'photo')]]" restamp>
                          <div class="content-row">
                            <iron-icon class="content-row-icon" icon="image:photo-camera"></iron-icon>
                            <div class="content-row-item">
                              <div class="content-row-label">Photo Capture Types</div>
                              <div class="content-row-item">
                                <paper-checkbox style="padding-bottom:8px;" checked="{{item.device_photo_allowed}}">Device</paper-checkbox>
                                <div style="display: flex;">
                                  <paper-checkbox checked="{{item.external_photo_allowed}}">External</paper-checkbox>
                                  <katapult-icon
                                    icon="help"
                                    size="small"
                                    style="margin: 0 5px; cursor: pointer;"
                                    title="Photos taken externally will need to be manually classified"
                                  ></katapult-icon>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div class="content-row">
                            <iron-icon class="content-row-icon" icon="visibility"></iron-icon>
                            <paper-checkbox class="content-row-label" checked="{{item.showClassifiedPhotos}}"
                              >Show Classified Photos</paper-checkbox
                            >
                          </div>
                          <div class="content-row">
                            <iron-icon class="content-row-icon" icon="icons:explore"></iron-icon>
                            <paper-checkbox class="content-row-label" checked="{{item.compass}}">Show Compass</paper-checkbox>
                          </div>
                        </template>

                        <!--Photo Group Configuration Options-->
                        <template is="dom-if" if="[[equal(item.type, 'photo_group')]]" restamp>
                          <div class="content-row">
                            <iron-icon class="content-row-icon" icon="icons:explore"></iron-icon>
                            <paper-checkbox class="content-row-label" checked="{{item.compass}}">Show Compass</paper-checkbox>
                          </div>
                          <div class="content-row">
                            <iron-icon class="content-row-icon" icon="image:photo-library"></iron-icon>
                            <div class="content-row-item">
                              <div class="content-row-label">Photo Prompts</div>
                              <paper-table style="overflow: hidden; flex-grow: 1;" rounded white outline>
                                <paper-row raised-on-scroll grey>
                                  <paper-cell icon><iron-icon icon="icons:swap-vert"></iron-icon></paper-cell>
                                  <paper-cell icon></paper-cell>
                                  <paper-cell>Photo Title</paper-cell>
                                  <paper-cell>Classification</paper-cell>
                                  <paper-cell>Prompt Text</paper-cell>
                                  <paper-cell style="max-width: 85px;">Required</paper-cell>
                                  <paper-cell icon></paper-cell>
                                </paper-row>

                                <paper-table-scroll on-sort-changed="stepPhotoPromptsSorted">
                                  <template is="dom-if" if="[[!hasEntries(item.photo_prompts, item.photo_prompts.*)]]">
                                    <paper-row divider slot="before">
                                      <paper-cell center style="color: var(--paper-grey-600);">
                                        <iron-icon icon="katapult-misc:search-off"></iron-icon>
                                        <span>No Photo Prompts Yet</span>
                                      </paper-cell>
                                    </paper-row>
                                  </template>
                                  <template is="dom-if" if="[[hasEntries(item.photo_prompts, item.photo_prompts.*)]]">
                                    <template is="dom-repeat" items="{{item.photo_prompts}}" as="photo" index-as="photoIndex">
                                      <paper-row white divider no-click-row slot="sortable">
                                        <paper-cell icon>
                                          <span>[[photo.order]]</span>
                                        </paper-cell>
                                        <paper-cell drag-handle icon>
                                          <iron-icon icon="katapult-misc:drag-indicator"></iron-icon>
                                        </paper-cell>
                                        <paper-cell>
                                          <paper-input label="Photo Title" value="{{photo.title}}" no-label-float></paper-input>
                                        </paper-cell>
                                        <paper-cell>
                                          <katapult-drop-down
                                            label="Select a Classification"
                                            items="[[getChipAttributes(item.attribute)]]"
                                            label-function="[[boundChipAttributeLabelFunction]]"
                                            value="{{photo.classification}}"
                                            no-label-float
                                            step-index="[[stepIndex]]"
                                            on-selected-changed="photoPromptClassificationChanged"
                                          ></katapult-drop-down>
                                        </paper-cell>
                                        <paper-cell>
                                          <paper-input label="Prompt Text" value="{{photo.prompt}}" no-label-float></paper-input>
                                        </paper-cell>
                                        <paper-cell style="max-width: 85px; justify-content: center;">
                                          <paper-checkbox
                                            style="--primary-color: var(--paper-red-500);"
                                            step-index="[[stepIndex]]"
                                            no-label
                                            checked="{{photo.required}}"
                                            on-checked-changed="updateSomeRequired"
                                          ></paper-checkbox>
                                        </paper-cell>
                                        <paper-cell icon-button step-index="[[stepIndex]]" on-click="deletePhotoPrompt">
                                          <katapult-button iconOnly noBorder icon="delete"></katapult-button>
                                        </paper-cell>
                                      </paper-row>
                                    </template>
                                  </template>
                                </paper-table-scroll>

                                <paper-row ripple short on-click="addPhotoPrompt">
                                  <paper-cell center pointer style="color: var(--paper-grey-600);">
                                    <span>ADD PHOTO PROMPT</span>
                                    <iron-icon style="margin-left: 8px;" icon="add-circle-outline"></iron-icon>
                                  </paper-cell>
                                </paper-row>
                              </paper-table>
                            </div>
                          </div>
                        </template>

                        <!-- Drawing Tool Configuration Options-->
                        <template is="dom-if" if="[[equal(item.type, 'drawing_tool')]]" restamp>
                          <div class="content-row">
                            <katapult-icon class="content-row-icon" icon="move_down"></katapult-icon>
                            <div class="content-row-item">
                              <div class="content-row-label">Drawing Tool Options</div>
                              <paper-checkbox checked="{{item.assess_new_node}}">Continue assessment on drawn node</paper-checkbox>
                              <paper-checkbox checked="{{item.auto_open_tool}}">Automatically open tool on step entry</paper-checkbox>
                            </div>
                          </div>
                        </template>

                        <!--Actions Table-->
                        <div class="content-row">
                          <!--FEAT: Find icon specified in design... add to katapult-misc in katapult-map-icons-->
                          <iron-icon class="content-row-icon" icon="image:flash-on"></iron-icon>
                          <div class="content-row-item">
                            <div class="content-row-label">Step Actions</div>
                            <paper-table style="overflow: hidden; flex-grow: 1;" rounded white outline>
                              <paper-row raised-on-scroll grey>
                                <paper-cell icon></paper-cell>
                                <paper-cell>Trigger</paper-cell>
                                <paper-cell>Action</paper-cell>
                                <paper-cell>Details</paper-cell>
                                <paper-cell></paper-cell>
                                <paper-cell icon></paper-cell>
                              </paper-row>

                              <paper-table-scroll disabled>
                                <template is="dom-if" if="[[!hasEntries(item.$actions, item.$actions.*)]]">
                                  <paper-row divider slot="before">
                                    <paper-cell center style="color: var(--paper-grey-600);">
                                      <iron-icon icon="katapult-misc:search-off"></iron-icon>
                                      <span>No Actions Yet</span>
                                    </paper-cell>
                                  </paper-row>
                                </template>
                                <template is="dom-if" if="[[hasEntries(item.$actions, item.$actions.*)]]">
                                  <template is="dom-repeat" items="{{item.$actions}}" as="action" index-as="actionIndex">
                                    <paper-row expand white divider no-click-row slot="sortable">
                                      <paper-cell icon>
                                        <iron-icon icon="[[getActionIcon(action.action)]]"></iron-icon>
                                      </paper-cell>
                                      <paper-cell>
                                        <katapult-drop-down
                                          label="Select a Trigger"
                                          items="[[getStepActionTriggers(item, item.*)]]"
                                          value="{{action.trigger}}"
                                          label-path="label"
                                          value-path="value"
                                          no-label-float
                                        ></katapult-drop-down>
                                      </paper-cell>
                                      <paper-cell>
                                        <katapult-drop-down
                                          label="Select an Action"
                                          items="[[stepActionTypes]]"
                                          value="{{action.action}}"
                                          label-function="[[camelCase]]"
                                          no-label-float
                                          on-selected-will-change="stepActionActionChanged"
                                        ></katapult-drop-down>
                                      </paper-cell>
                                      <!--Jump Details-->
                                      <template is="dom-if" if="[[equal(action.action, 'jump_to_step')]]" restamp>
                                        <paper-cell>
                                          <!--TODO: Does not visually update when getJumpTargets array changes (due to sort or other change)-->
                                          <katapult-drop-down
                                            label="Select Jump Target"
                                            items="[[getJumpTargets(item, stepsArray.*)]]"
                                            value="{{action.detail_1}}"
                                            label-path="label"
                                            value-path="value"
                                            no-label-float
                                          ></katapult-drop-down>
                                        </paper-cell>
                                        <paper-cell></paper-cell>
                                      </template>
                                      <!--Set Attribute Details-->
                                      <template is="dom-if" if="[[equal(action.action, 'set_an_attribute')]]" restamp>
                                        <paper-cell>
                                          <katapult-drop-down
                                            label="Select an Attribute"
                                            items="[[otherAttributes]]"
                                            label-function="[[camelCaseKey]]"
                                            value-path="$key"
                                            value="{{action.detail_1}}"
                                            no-label-float
                                            on-selected-will-change="stepActionAttributeChanged"
                                          >
                                            <paper-item on-click="createNewAttribute">CREATE NEW ATTRIBUTE</paper-item>
                                          </katapult-drop-down>
                                        </paper-cell>
                                        <paper-cell>
                                          <input-element
                                            model="[[getAttributeModel(action.detail_1)]]"
                                            value="{{action.detail_2}}"
                                            other-attributes="[[otherAttributes]]"
                                            user-group="[[userGroup]]"
                                            no-label-float
                                            camel-case
                                            hide-attribute-name
                                            style="flex: 1;"
                                            job-creator="[[modelSpaceDetails.$key]]"
                                          ></input-element>
                                        </paper-cell>
                                      </template>
                                      <!--Empty Cells-->
                                      <template is="dom-if" if="[[noActionDetails(action.action)]]">
                                        <paper-cell></paper-cell>
                                        <paper-cell></paper-cell>
                                      </template>
                                      <paper-cell icon-button step-index="[[stepIndex]]" on-click="deleteStepAction">
                                        <katapult-button iconOnly noBorder icon="delete"></katapult-button>
                                      </paper-cell>
                                    </paper-row>
                                  </template>
                                </template>
                              </paper-table-scroll>

                              <paper-row ripple short on-click="addStepAction">
                                <paper-cell center pointer style="color: var(--paper-grey-600);">
                                  <span>ADD ACTION</span>
                                  <iron-icon style="margin-left: 8px;" icon="add-circle-outline"></iron-icon>
                                </paper-cell>
                              </paper-row>
                            </paper-table>
                          </div>
                        </div>
                      </div>
                    </paper-header-content>
                  </template>
                </paper-table-scroll>

                <paper-row ripple short on-click="addAssessmentStep">
                  <paper-cell center pointer style="color: var(--paper-grey-600);">
                    <span>ADD STEP</span>
                    <iron-icon style="margin-left: 8px;" icon="add-circle-outline"></iron-icon>
                  </paper-cell>
                </paper-row>
              </paper-table>
              <div hidden$="[[!hasDrawingToolStep(stepsArray.*)]]">* An assessment should have only one Drawing Tool step</div>
            </div>
          </template>

          <template is="dom-if" if="[[equal(step.id, 'assessment_actions')]]" restamp>
            <katapult-button on-click="nextStep" slot="current-step-header">Continue</katapult-button>
            <paper-table style="overflow: hidden; flex-grow: 1;" rounded white outline slot="body">
              <paper-row raised-on-scroll grey>
                <paper-cell icon></paper-cell>
                <paper-cell>Trigger</paper-cell>
                <paper-cell>Action</paper-cell>
                <paper-cell>Details</paper-cell>
                <paper-cell>Value</paper-cell>
                <paper-cell style="max-width: 85px;">Overwrite</paper-cell>
                <paper-cell icon></paper-cell>
              </paper-row>

              <paper-table-scroll disabled>
                <template is="dom-if" if="[[!numAssessmentActions]]">
                  <paper-row divider slot="before">
                    <paper-cell center style="color: var(--paper-grey-600);">
                      <iron-icon icon="katapult-misc:search-off"></iron-icon>
                      <span>No Actions Yet</span>
                    </paper-cell>
                  </paper-row>
                </template>
                <template
                  is="dom-repeat"
                  items="{{stepsArray}}"
                  as="action"
                  index-as="actionIndex"
                  filter="isAction"
                  rendered-item-count="{{numAssessmentActions}}"
                >
                  <paper-row expand white divider no-click-row slot="sortable">
                    <paper-cell icon>
                      <iron-icon icon="[[getActionIcon(action.action)]]"></iron-icon>
                    </paper-cell>
                    <paper-cell>
                      <katapult-drop-down
                        items="[[assessmentActionTriggers]]"
                        label-path="$val"
                        value-path="$key"
                        label="Select a Trigger"
                        value="{{action.trigger}}"
                        no-label-float
                      ></katapult-drop-down>
                    </paper-cell>
                    <!--Hard-wired for 'Set An Attribute' as it is the only currently supported action-->
                    <paper-cell>
                      <span>Set An Attribute</span>
                    </paper-cell>
                    <paper-cell>
                      <katapult-drop-down
                        label="Select an Attribute"
                        items="[[otherAttributes]]"
                        label-function="[[camelCaseKey]]"
                        value-path="$key"
                        value="{{action.detail_1}}"
                        no-label-float
                        on-selected-will-change="assessmentActionAttributeChanged"
                      >
                        <paper-item on-click="createNewAttribute">CREATE NEW ATTRIBUTE</paper-item>
                      </katapult-drop-down>
                    </paper-cell>
                    <paper-cell>
                      <input-element
                        model="[[getAttributeModel(action.detail_1)]]"
                        value="{{action.detail_2}}"
                        other-attributes="[[otherAttributes]]"
                        user-group="[[userGroup]]"
                        no-label-float
                        camel-case
                        hide-attribute-name
                        style="flex: 1;"
                        job-creator="[[modelSpaceDetails.$key]]"
                      ></input-element>
                    </paper-cell>
                    <paper-cell style="max-width: 85px; justify-content: center;">
                      <template is="dom-if" if="[[equal(action.trigger, 'on_create')]]">
                        <paper-checkbox no-label checked disabled></paper-checkbox>
                      </template>
                      <template is="dom-if" if="[[!equal(action.trigger, 'on_create')]]">
                        <paper-checkbox
                          style="--primary-color: var(--paper-red-500);"
                          no-label
                          checked="{{action.overwrite}}"
                        ></paper-checkbox>
                      </template>
                    </paper-cell>
                    <paper-cell icon-button on-click="deleteAssessmentAction">
                      <katapult-button iconOnly noBorder icon="delete"></katapult-button>
                    </paper-cell>
                  </paper-row>
                </template>
              </paper-table-scroll>

              <paper-row ripple short on-click="addAssessmentAction">
                <paper-cell center pointer style="color: var(--paper-grey-600);">
                  <span>ADD ACTION</span>
                  <iron-icon style="margin-left: 8px;" icon="add-circle-outline"></iron-icon>
                </paper-cell>
              </paper-row>
            </paper-table>
          </template>

          <template is="dom-if" if="[[equal(step.id, 'toolsets')]]" restamp>
            <katapult-button on-click="nextStep" slot="current-step-header">Skip</katapult-button>
            <div style="width: 400px; color: var(--primary-text-color-faded);" slot="body">
              <paper-table light-grey rounded outline>
                <paper-row input>
                  <paper-cell>
                    <katapult-drop-down
                      items="[[toolsets]]"
                      label-function="[[camelCaseKey]]"
                      value-path="$key"
                      label="Select a Toolset"
                      no-label-float
                      on-selected-changed="toolsetSelectedChanged"
                    ></katapult-drop-down>
                  </paper-cell>
                </paper-row>
              </paper-table>
              <template is="dom-if" if="[[nullify(_itemData.toolsets, _itemData.toolsets.*)]]">
                <paper-table style="margin-top: 24px;" rounded outline white dividers>
                  <template is="dom-repeat" items="[[toArray(_itemData.toolsets, _itemData.toolsets.*)]]">
                    <paper-row>
                      <paper-cell>[[camelCase(item.$key)]]</paper-cell>
                      <paper-cell icon-button>
                        <katapult-button iconOnly noBorder icon="delete" on-click="removeFromToolset"></katapult-button>
                      </paper-cell>
                    </paper-row>
                  </template>
                </paper-table>
              </template>
            </div>
          </template>
        </katapult-editor-step>
      </template>

      <katapult-dialog id="changeWarningDialog" title="Configuration Warning" max-width="600">
        <div style="display: flex; flex-direction: column; gap: 16px;">
          <template is="dom-if" if="[[equal(dialogWarning, 'deleting_step')]]">
            <div>Deleting this step will delete its configurations and any attached prompt images.</div>
          </template>
          <template is="dom-if" if="[[equal(dialogWarning, 'changing_step_type')]]">
            <div>Changing this step's type will result in type-specific configurations being lost.</div>
          </template>
          <template is="dom-if" if="[[equal(dialogWarning, 'changing_step_attribute')]]">
            <div>Changing this step's attribute will result in some step actions being deleted.</div>
          </template>
          <template is="dom-if" if="[[equal(dialogWarning, 'changing_step_picklist')]]">
            <div>Changing this step's picklist will result in some step actions being deleted.</div>
          </template>
          <template is="dom-if" if="[[brokenJumps.length]]">
            <div>Committing this change will break the following jumps. If you continue, they will be deleted.</div>
            <template is="dom-repeat" items="[[brokenJumps]]" as="jump">
              <div style="display: flex; justify-content: center; align-items: center; gap: 16px;">
                <div>
                  <span>[[jump.source]]</span>
                  <span hidden="[[!jump.trigger]]"> ([[jump.trigger]])</span>
                </div>
                <iron-icon icon="icons:trending-flat" style="margin: 0 16px;"></iron-icon>
                <div>[[jump.targetTitle]]</div>
              </div>
            </template>
          </template>
          <div>Do you want to continue with this change?</div>
        </div>
        <div slot="buttons" style="width: 100%; display: flex; justify-content: space-between;">
          <katapult-button dialog-dismiss outline>Cancel</katapult-button>
          <katapult-button dialog-dismiss color="var(--secondary-color)" on-click="confirmWarningDialogAction">Continue</katapult-button>
        </div>
      </katapult-dialog>

      <input id="imageSelect" type="file" hidden accept="image/png, image/jpeg" on-change="imageSelectChanged" />
      ${super.createItemContainer}
    `;
  }

  static get properties() {
    return {
      assessmentTypes: {
        type: Array,
        value: () => [
          { type: 'default', title: 'Assess Existing and Draw New Nodes (default)' },
          { type: 'existing_only', title: 'Assess Existing Nodes' },
          { type: 'existing_sections_only', title: 'Assess Existing Sections' },
          { type: 'existing_nodes_and_sections', title: 'Assess Existing Nodes and Sections' },
          { type: 'new_only', title: 'Draw New Nodes' }
        ]
      },
      backup: {
        type: Object,
        value: {}
      },
      stepTypes: {
        type: Object,
        computed: 'computeStepTypes(drawingToolStepsAllowed, stepsArray.*)'
      },
      stepActionTypes: {
        type: Array,
        value: ['jump_to_step', 'end_assessment', 'set_an_attribute']
      },
      oldTriggerTypes: {
        type: Object,
        value: () => {
          return {
            stamp_on_create: 'on_create',
            stamp_on_select: 'on_select',
            stamp_on_complete: 'on_finish'
          };
        }
      },
      stepsArray: {
        type: Array,
        value: []
      },
      assessmentActionTriggers: {
        type: Object,
        computed: 'getAssessmentActionTriggers(_itemData.assessment_permission)'
      },
      displayCache: {
        type: Array,
        value: []
      },
      generateTitles: {
        type: Boolean,
        value: true
      },
      katapultDropDownScrollTarget: {
        type: Boolean,
        value: true,
        reflectToAttribute: true
      },
      addressComponents: {
        type: Array,
        value: [
          { title: 'Street Number', model_attribute: 'street_number' },
          { title: 'Street Name', model_attribute: 'street_name' },
          { title: 'Township', model_attribute: 'township' },
          { title: 'Municipality', model_attribute: 'municipality' },
          { title: 'County', model_attribute: 'county' },
          { title: 'State', model_attribute: 'state' },
          { title: 'Zip Code', model_attribute: 'zip_code' }
        ]
      },
      stepOptions: {
        type: Array,
        value: [{ title: 'State Abbreviation', model_attribute: 'state_abbreviation', step_type: 'set_address_data' }]
      },
      drawingTools: {
        type: Array,
        computed: 'computeDrawingTools(tools.*)'
      },
      drawingToolStepsAllowed: {
        type: Boolean,
        value: false
      },
      otherAttributes: {
        type: Object,
        value: () => {}
      },
      attributeKeysFilteredByType: {
        type: Array,
        computed: 'getAttributeKeysFilteredByType(_itemData.assessment_permission)'
      }
    };
  }

  static get observers() {
    return ['assessmentModelChanged(_itemData.*)', 'stepsArrayChanged(stepsArray.*)'];
  }

  constructor() {
    super();

    this.tools = {};
    this.stepsArray = [];
    this.drawingTools = [];
    this.brokenJumps = [];
    this.generateTitles = true;

    // Template helpers
    this.hasDrawingToolStep = () => this.stepsArray.some((step) => step.type == 'drawing_tool');
  }

  ready() {
    super.ready();

    this.keyMatchLabel = true;

    // An ordered list of all possible steps.
    this.setSteps([
      {
        id: 'assessment_style',
        title: 'Assessment Style'
      },
      {
        id: 'assessment_permission',
        title: 'Assessment Permission (changing this option may clear Set Attribute steps)'
      },
      {
        id: 'assessment_steps',
        title: 'Assessment Steps'
      },
      {
        id: 'assessment_actions',
        title: 'Assessment Actions'
      },
      {
        id: 'toolsets',
        title: 'Toolsets',
        trigger: () => this.get('_itemData.toolsets') != null
      }
    ]);

    this.boundChipAttributeLabelFunction = this.chipAttributeLabelFunction.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();

    // Set up a feature flag listener for drawing tool steps
    this._featureFlagListeners = {
      // TODO (2024-07-18): This can be removed when drawing tool steps are generally available
      assessment_drawing_tool_steps: addFlagListener('assessment_drawing_tool_steps', (value) => (this.drawingToolStepsAllowed = value))
    };
  }

  disconnectedCallback() {
    for (const key in this._featureFlagListeners) {
      removeFlagListener(key, this._featureFlagListeners[key]);
    }

    super.disconnectedCallback();
  }

  computeStepTypes() {
    const showDrawingToolOption = this.drawingToolStepsAllowed || this.stepsArray?.some((step) => step.type == 'drawing_tool');
    return {
      attribute: 'Set Attribute',
      photo: 'Take Photo',
      photo_group: 'Take Multiple Photos',
      set_location: 'Set Location',
      set_address_data: 'Set Address Data',
      // TODO (2024-07-18): This can be removed when drawing tool steps are generally available
      ...(showDrawingToolOption && { drawing_tool: 'Call Drawing Tool (BETA)' })
    };
  }

  labelChanged(label) {
    this.set('_itemData.label', label);
  }

  chooseColor() {
    this.$.colorPickerDialog.color = this.get('_itemData.color');
    this.$.colorPickerDialog.open();
  }

  chooseIcon() {
    this.$.iconPickerDialog.open();
  }

  colorSelected(e) {
    this.set('_itemData.color', e.detail.color);
  }

  iconSelected(e) {
    this.set('_itemData.icon', e.detail.icon);
  }

  _initializeNewItem() {
    this.set('_itemData.icon', 'create');
    this.set('_itemData.color', '#000');
    this.set('_itemData.assessment_permission', 'default');
    this.set('_itemData.view', 'assessment');
    this.set('_itemData.node', {});
    this.set('_itemData.node.location', 'new');
    this.set('_itemData.node.open_for_editing', true);
    this.set('_itemData.node.attributes', {});
  }

  createNewAttribute(e) {
    e.currentTarget.parentElement.close();
    document.dispatchEvent(new CustomEvent('create-new-attribute'));
  }

  getAttributeModel(property) {
    return this.otherAttributes?.[property];
  }

  getAttributePicklists(property) {
    return Object.keys(this.getAttributeModel(property).picklists || {});
  }

  hasEntries(array) {
    return Array.isArray(array) && array.length > 0;
  }

  toArray(x) {
    return ToArray(x);
  }

  // FEAT: Maybe this should be a k function? Maybe it already is?
  snakeCase(str) {
    let snakeStr = str.trim().split(' ');
    for (const key in snakeStr) {
      if (snakeStr[key] !== snakeStr[key].toUpperCase()) {
        snakeStr[key] = snakeStr[key].toLowerCase();
      }
    }
    return snakeStr.join('_');
  }

  isStep(item) {
    return !!item.order;
  }

  isAction(item) {
    return !item.order;
  }

  isDropdown(type) {
    return ['dropdown', 'multi_dropdown'].includes(type);
  }

  getItemKey(item) {
    return this.isStep(item)
      ? `${item.order.toString().padStart(3, '0')}_${this.snakeCase(item.title || '')}`
      : `${item.trigger || this.oldTriggerTypes[item.type]}_${item.detail_1 || item.attribute || ''}`;
  }

  toggleExpandAll(e) {
    let allExpanded = this.stepsArray.filter((step) => step.$opened).length == this.numAssessmentSteps;
    for (const key in this.stepsArray) if (this.isStep(this.stepsArray[key])) this.set(`stepsArray.${key}.$opened`, !allExpanded);
  }

  getStepIcon(step) {
    switch (step.type) {
      case 'attribute':
        switch (step.$gui_element) {
          case 'dropdown':
            return 'menu';
          case 'multi_dropdown':
            return 'playlist_add_check';
          case 'checkbox':
            return 'check_box';
          default:
            return 'input';
        }
      case 'photo':
        return 'photo_camera';
      case 'set_location':
        return 'my_location';
      case 'set_address_data':
        return 'pin_drop';
      case 'photo_group':
        return 'camera_roll';
      case 'drawing_tool':
        return 'draw';
      default:
        return 'more_horiz';
    }
  }

  hasHeaderRowDetails(stepType, key) {
    // If the item type is one of the specials, return true if the key is else; otherwise, return when step type and key are equal
    return ['set_location', 'photo_group', 'set_address_data', '', null].includes(stepType) ? key == '$else' : stepType == key;
  }

  assessmentPermissionChanged(e) {
    // Set item data accordingly
    this.set(`_itemData.assessment_permission`, e.detail.value);
  }

  stepsArraySorted(e) {
    // Make a copy of steps array and mutate it as per the sort
    const copyOfStepsArray = this.stepsArray.slice();
    const removedItem = copyOfStepsArray.splice(e.detail.prevIndex, 1);
    copyOfStepsArray.splice(e.detail.index, 0, removedItem[0]);

    // Check the mutated copy for broken jumps
    this.brokenJumps = this.getBrokenJumps(copyOfStepsArray);
    this.dialogWarning = 'changing_step_order';
    this.actionData = { prevIndex: e.detail.prevIndex, index: e.detail.index };

    // If there are broken jumps, show the warning dialog
    if (this.brokenJumps.length > 0) {
      /** @type {import('../katapult-elements/katapult-dialog.js').KatapultDialog} */
      (this.$.changeWarningDialog).open();
    }
    // Otherwise, commit the sort automatically
    else this.sortStepsArray();
  }

  sortStepsArray() {
    const { prevIndex, index } = this.actionData ?? {};
    let movedItems = [];

    // Depending on whether the step is being moved forward or back, rearrange the relevant portion of stepsArray
    if (prevIndex > index) movedItems = [this.stepsArray[prevIndex], ...this.stepsArray.slice(index, prevIndex)];
    else movedItems = [...this.stepsArray.slice(prevIndex + 1, index + 1), this.stepsArray[prevIndex]];

    // Splice in the rearranged array, reorder the steps, and delete any broken jumps
    this.splice(`stepsArray`, Math.min(index, prevIndex), movedItems.length, ...movedItems);
    this.reorderSteps();
    this.deleteBrokenJumps();
  }

  addAssessmentStep(e) {
    let order = this.numAssessmentSteps + 1;
    this.splice('stepsArray', this.numAssessmentSteps, 0, {
      $actions: [],
      uid: FirebaseWorker.ref().push().key,
      title: `Step ${order}`,
      order: order,
      type: '',
      required: false,
      prompt: '',
      image: null
    });
  }

  promptDeleteAssessmentStep(e) {
    // Make a copy of steps array and mutate it as per the delete
    const copyOfStepsArray = this.stepsArray.slice();
    copyOfStepsArray.splice(e.model.stepIndex, 1);

    // Check the mutated copy for broken jumps
    this.brokenJumps = this.getBrokenJumps(copyOfStepsArray);
    this.dialogWarning = 'deleting_step';
    this.actionData = { stepIndex: e.model.stepIndex };

    /** @type {import('../katapult-elements/katapult-dialog.js').KatapultDialog} */
    (this.$.changeWarningDialog).open();
  }

  confirmWarningDialogAction() {
    switch (this.dialogWarning) {
      case 'changing_step_order':
        this.sortStepsArray();
        break;
      case 'deleting_step':
        this.deleteAssessmentStep();
        break;
      case 'changing_step_type':
        this.changeStepType();
        break;
      case 'changing_step_attribute':
        this.changeStepAttribute();
        break;
      case 'changing_step_picklist':
        this.changeStepPicklist();
        break;
    }
  }

  deleteBrokenJumps() {
    this.brokenJumps.forEach((jump) => this.splice(`stepsArray.${jump.stepIndex}.$actions`, jump.actionIndex, 1));
    this.brokenJumps = [];
  }

  deleteAssessmentStep() {
    const { stepIndex } = this.actionData ?? {};

    // Delete the step's prompt image, if it has one
    if (this.stepsArray[stepIndex].image) this.deleteImage({ model: { stepIndex: stepIndex } });

    // Delete the step, reorder the other steps, and delete any broken jumps
    this.splice('stepsArray', stepIndex, 1);
    this.reorderSteps();
    this.deleteBrokenJumps();
  }

  reorderSteps() {
    for (const key in this.stepsArray) {
      if (this.isStep(this.stepsArray[key])) this.set(`stepsArray.${key}.order`, parseInt(key, 10) + 1);
    }
  }

  addressComponentAttributeChanged(e) {
    let modelAttribute = e?.model?.addressComponent?.model_attribute;
    if (modelAttribute) {
      let index = e.model.stepIndex;
      Path.set(this.stepsArray, `${index}.address_attributes.${modelAttribute}`, e.detail.value || null);
      this.notifyPath(`stepsArray.${index}.address_attributes.${modelAttribute}`);
    }
  }
  addressComponentOptionChanged(e) {
    let modelAttribute = e?.model?.option?.model_attribute;
    if (modelAttribute) {
      let index = e.model.stepIndex;
      Path.set(this.stepsArray, `${index}.${modelAttribute}`, e.detail.value || null);
      this.notifyPath(`stepsArray.${index}.${modelAttribute}`);
    }
  }

  getAddressComponentAttribute(uid, addressComponent) {
    if (addressComponent) {
      let index = this.getStepIndex(uid);
      return this.stepsArray?.[index]?.address_attributes?.[addressComponent.model_attribute];
    }
  }
  getAddressComponentOption(uid, option) {
    if (option) {
      let index = this.getStepIndex(uid);
      return this.stepsArray[index]?.[option?.model_attribute];
    }
  }

  stepTypeChanged(e) {
    // Don't do anything if the value didn't change
    if (e.detail.previousValue == e.detail.value) return;

    // We are using a special handler, so we don't want the selection done automatically
    e.composedPath()[0].preventSelection = true;

    // Set the dialog warning and broken jumps (we know there are none)
    this.dialogWarning = 'changing_step_type';
    this.brokenJumps = [];
    this.actionData = { stepIndex: e.model.stepIndex, newType: e.detail.value };

    // If there was a previous value, show the dialog
    if (e.detail.previousValue && e.detail.value != e.detail.previousValue) {
      /** @type {import('../katapult-elements/katapult-dialog.js').KatapultDialog} */
      (this.$.changeWarningDialog).open();
    }
    // Otherwise, commit the change automatically
    else this.changeStepType();
  }

  changeStepType() {
    const { stepIndex, newType } = this.actionData ?? {};

    const oldStep = this.stepsArray[stepIndex];
    const newStep = {
      $actions: oldStep.$actions.filter((a) => a.trigger == '$advance'),
      title: oldStep.title,
      attribute: oldStep.attribute,
      order: oldStep.order,
      type: newType,
      required: oldStep.required,
      prompt: oldStep.prompt,
      image: oldStep.image,
      uid: oldStep.uid || globalThis.FirebaseWorker.ref().push().key,
      ...(newType == 'attribute' && {
        $gui_element: null,
        picklist: null
      }),
      ...(newType == 'photo' && {
        classification: null,
        compass: false,
        device_photo_allowed: true,
        external_photo_allowed: false
      }),
      ...(newType == 'photo_group' && {
        compass: false,
        device_photo_allowed: false,
        external_photo_allowed: true,
        photo_prompts: null,
        required: null
      }),
      ...(newType == 'set_address_data' && {
        address_attributes: {}
      })
    };

    this.set(`stepsArray.${stepIndex}`, newStep);
  }

  stepAttributeChanged(e) {
    // We are using a special handler, so we don't want the selection done automatically
    e.composedPath()[0].preventSelection = true;

    // Set the dialog warning and broken jumps (we know there are none)
    this.dialogWarning = 'changing_step_attribute';
    this.brokenJumps = [];
    this.actionData = { stepIndex: e.model.stepIndex, step: e.model.item, attribute: e.detail.value };

    // If attribute had a previous (significant) value, we need to confirm that we want to discard existing data
    const hasNonDefaultActions = e.model.item.$actions.filter((a) => a.trigger != '$advance').length > 0;
    if (e.detail.previousValue && e.detail.value != e.detail.previousValue && hasNonDefaultActions) {
      /** @type {import('../katapult-elements/katapult-dialog.js').KatapultDialog} */
      (this.$.changeWarningDialog).open();
    }
    // Otherwise, just commit the change
    else this.changeStepAttribute();
  }

  changeStepAttribute() {
    const { stepIndex, step, attribute } = this.actionData ?? {};

    this.set(`stepsArray.${stepIndex}.attribute`, attribute);
    // Filter out old picklist-triggered jumps
    this.set(
      `stepsArray.${stepIndex}.$actions`,
      step.$actions.filter((a) => a.trigger == '$advance')
    );
    // If we are generating step titles and we have an atribute value, set the title
    if (this.generateTitles && step.title.startsWith('Step') && !!attribute) {
      this.set(`stepsArray.${stepIndex}.title`, CamelCase(attribute));
    }

    // Get the attribute's gui element
    let guiElement = this.getAttributeModel(attribute)?.gui_element;
    this.set(`stepsArray.${stepIndex}.$gui_element`, guiElement);
    // Set picklist to null by default
    this.set(`stepsArray.${stepIndex}.picklist`, null);

    // If the gui element is a picklist and there is only one picklist, auto-select it
    if (this.isDropdown(guiElement)) {
      const picklists = this.getAttributePicklists(attribute);
      if (picklists.length == 1) this.set(`stepsArray.${stepIndex}.picklist`, picklists[0]);
    }
  }

  stepPicklistChanged(e) {
    // We are using a special handler, so we don't want the selection done automatically
    e.composedPath()[0].preventSelection = true;

    // Set the dialog warning and broken jumps (we know there are none)
    this.dialogWarning = 'changing_step_picklist';
    this.brokenJumps = [];
    this.actionData = { stepIndex: e.model.stepIndex, step: e.model.item, picklist: e.detail.value };

    // If attribute had a previous (significant) value, we need to confirm that we want to discard existing data
    const hasNonDefaultActions = e.model.item.$actions.filter((a) => a.trigger != '$advance').length > 0;
    if (e.detail.previousValue && e.detail.value != e.detail.previousValue && hasNonDefaultActions) {
      /** @type {import('../katapult-elements/katapult-dialog.js').KatapultDialog} */
      (this.$.changeWarningDialog).open();
    }
    // Otherwise, just commit the change
    else this.changeStepPicklist();
  }

  changeStepPicklist() {
    const { stepIndex, step, picklist } = this.actionData ?? {};
    this.set(`stepsArray.${stepIndex}.picklist`, picklist);
    // Filter out old picklist-triggered jumps
    this.set(
      `stepsArray.${stepIndex}.$actions`,
      step.$actions.filter((a) => a.trigger == '$advance')
    );
  }

  hidePicklistSelect(step) {
    return this.getAttributePicklists(step.attribute).length <= 1;
  }

  stepClassificationChanged(e) {
    let stepTitle = Path.get(e, 'model.item.title') || '';
    if (this.generateTitles && stepTitle.startsWith('Step') && e.detail.value)
      this.set(`stepsArray.${e.model.stepIndex}.title`, CamelCase(e.detail.value));
  }

  getChipAttributes() {
    let chipAttributes = [];
    for (const key in this.inputModels) {
      if (!this.inputModels?.[key]?._attributes?.pixel_selection) chipAttributes.push(key);
    }
    return chipAttributes;
  }

  chipAttributeLabelFunction(inputModelKey) {
    if (!inputModelKey) return;
    const inputModel = this.inputModels?.[inputModelKey];
    return inputModel?.name ?? CamelCase(inputModelKey);
  }

  openImageSelect(e) {
    if (this.$.imageSelect) {
      this.$.imageSelect.stepIndex = e.model.stepIndex;
      this.$.imageSelect.click();
    }
  }

  getAttributeKeys(attrs, type, gui_element) {
    let t = [];
    if (type) {
      for (let a in attrs) {
        let x = attrs[a].attribute_types;
        if ((Array.isArray(x) && x.includes(type)) || x == null) {
          // filter down to specific types of attributes
          if (gui_element) {
            if (attrs[a].gui_element == gui_element) t.push(a);
          } else t.push(a);
        }
      }
    }
    return t;
  }

  imageSelectChanged(e) {
    let index = e.currentTarget.stepIndex;
    let inputFiles = [];
    let file = null;
    // Try to get the file
    if (e.dataTransfer == null) inputFiles = e.target.files;
    else inputFiles = e.dataTransfer.files;
    // Provided inputFiles is not null
    if (inputFiles != null) file = inputFiles[0];
    // If file is null or undefined, return without continuing
    if (!file) return;
    // Check that the file size is less than 40MB
    if (file.size > 100000000) {
      console.warn('Image uploads are limited to files less than 100mb');
      return;
    }
    // Add job to imageUploadJobs
    this.set(`stepsArray.${index}.$upload`, {
      status: `Uploading ${file.name}`,
      progress: 0
    });
    // Generate a unique key for the file
    let fileKey = FirebaseWorker.ref('/').push().key;
    // Create the file path
    let path = `company_files/${this.userGroup}/${fileKey}`;
    // Create an upload task
    let uploadTask = firebase.storage().ref(path).put(file);
    // Watch for changes with the upload
    uploadTask.on('state_changed', (s) => {
      // Update the progress bar
      let progress = Math.round((s.bytesTransferred / s.totalBytes) * 100);
      this.set(`stepsArray.${index}.$upload.progress`, progress);
    });
    // Watch for the upload to finish
    uploadTask.then((s) => {
      // Add the file data in the step
      this.set(`stepsArray.${index}.image`, {
        name: file.name,
        size: file.size,
        type: file.type,
        upload_date: Date.now(),
        path: path
      });
      this.set(`stepsArray.${index}.$upload`, null);
    });
  }

  getImageSize(size) {
    let sizeInMB = size / 1000000;
    return sizeInMB.toFixed(2);
  }

  deleteImage(e) {
    // Get the image's path from the step, and delete the image from firebase storage (and its data from the step)
    let path = this.get(`stepsArray.${e.model.stepIndex}.image.path`);
    if (path)
      firebase
        .storage()
        .ref(path)
        .delete()
        .then(() => this.set(`stepsArray.${e.model.stepIndex}.image`, null));
  }

  addPhotoPrompt(e) {
    let path = `stepsArray.${e.model.stepIndex}.photo_prompts`;
    let order = (this.get(path) || []).length + 1;
    let model = {
      order: order,
      title: `Prompt ${order}`,
      classification: null,
      prompt: null,
      required: false,
      uid: FirebaseWorker.ref().push().key
    };
    if (!this.get(path)) this.set(path, [{ ...model }]);
    else this.push(path, model);
  }

  photoPromptClassificationChanged(e) {
    if (this.generateTitles && e.model.photo.title.startsWith('Prompt') && !!e.detail.value) {
      this.set(`stepsArray.${e.currentTarget.stepIndex}.photo_prompts.${e.model.photoIndex}.title`, CamelCase(e.detail.value));
    }
  }

  deletePhotoPrompt(e) {
    this.splice(`stepsArray.${e.currentTarget.stepIndex}.photo_prompts`, e.model.photoIndex, 1);
    this.reorderPhotoPrompts(e.currentTarget.stepIndex);
  }

  reorderPhotoPrompts(index) {
    for (const key in this.stepsArray[index].photo_prompts) {
      this.set(`stepsArray.${index}.photo_prompts.${key}.order`, parseInt(key, 10) + 1);
    }
  }

  stepPhotoPromptsSorted(e) {
    let movedItems = [];
    // Get the array of photo prompts from the step
    let prompts = this.get(`stepsArray.${e.model.stepIndex}.photo_prompts`);
    // Depending on whether the prompt is being moved forward or back, rearrange the relevant portion of stepsArray
    if (e.detail.prevIndex > e.detail.index)
      movedItems = [prompts[e.detail.prevIndex], ...prompts.slice(e.detail.index, e.detail.prevIndex)];
    else movedItems = [...prompts.slice(e.detail.prevIndex + 1, e.detail.index + 1), prompts[e.detail.prevIndex]];
    // Splice in the rearranged array
    this.splice(
      `stepsArray.${e.model.stepIndex}.photo_prompts`,
      Math.min(e.detail.index, e.detail.prevIndex),
      movedItems.length,
      ...movedItems
    );
    this.reorderPhotoPrompts(e.model.stepIndex);
  }

  requireAllPhotoPrompts(e) {
    // Force the toggle to require all steps
    this.togglePhotoGroupRequired({ model: { stepIndex: e.model.stepIndex }, detail: { value: true } });
  }

  isPhotoGroupRequired(step) {
    return Array.isArray(step.photo_prompts) && step.photo_prompts.filter((p) => p.required).length == step.photo_prompts.length;
  }

  togglePhotoGroupRequired(e) {
    let path = `stepsArray.${e.model.stepIndex}.photo_prompts`;
    let prompts = this.get(path);
    for (const key in prompts) this.set(`${path}.${key}.required`, e.detail.value);
    this.set(`stepsArray.${e.model.stepIndex}.$some_required`, false);
  }

  updateSomeRequired(e) {
    let prompts = this.get(`stepsArray.${e.currentTarget.stepIndex}.photo_prompts`);
    this.set(`stepsArray.${e.currentTarget.stepIndex}.photo_prompts.${e.model.photoIndex}.required`, e.detail.value);
    let numRequired = prompts.filter((p) => p.required).length;
    // If some but not all of the prompts are required, set some_required to true
    this.set(`stepsArray.${e.currentTarget.stepIndex}.$some_required`, numRequired > 0 && numRequired < prompts.length);
  }

  addStepAction(e) {
    let path = `stepsArray.${e.model.stepIndex}.$actions`;
    let model = {
      trigger: null,
      action: null,
      detail_1: null,
      detail_2: null
    };
    // Check if the path exists; if not, create the array; otherwise, push to the array.
    if (!this.get(path)) this.set(path, [{ ...model }]);
    else this.push(path, model);
  }

  getActionIcon(action) {
    // FEAT: Use design-specified icons
    switch (action) {
      case 'jump_to_step':
        return 'icons:redo';
      case 'end_assessment':
        return 'icons:cancel';
      case 'set_an_attribute':
        return 'av:playlist-add';
      default:
        return 'icons:remove';
    }
  }

  getStepIndex(uid) {
    return this.stepsArray.findIndex((x) => x.uid == uid);
  }

  getStepActionTriggers(step) {
    let triggerList = [{ label: 'Step Completed', value: '$advance' }];
    // If the step is an attribute step
    if (step.type == 'attribute') {
      // Get potential dropdown selections
      switch (step.$gui_element) {
        case 'dropdown':
          let picklist = Path.get(this.getAttributeModel(step.attribute), `picklists.${step.picklist}`);
          for (const key in picklist) {
            triggerList.push({ label: 'Selected ' + picklist[key].value, value: picklist[key].value });
          }
          break;
        // We treat checkboxes as picklists for the purposes of mobile assessment
        case 'checkbox':
          triggerList.push({ label: 'Yes (True)', value: true });
          triggerList.push({ label: 'No (False)', value: false });
          break;
      }
    }
    return triggerList;
  }

  noActionDetails(action) {
    return !action || action == 'end_assessment';
  }

  getJumpTargets(currentStep) {
    let jumpTargets = [];
    // Get an array of all steps after the current step
    let jumpSteps = this.stepsArray.slice(currentStep.order, this.numAssessmentSteps);
    for (const key in jumpSteps) {
      jumpTargets.push({
        label: `(${jumpSteps[key].order}) ${jumpSteps[key].title}`,
        value: jumpSteps[key].uid
      });
    }
    return jumpTargets;
  }

  getBrokenJumps(stepsArray) {
    // Function optionally accepts a version of stepsArray as a parameter
    stepsArray = stepsArray || this.stepsArray;
    let invalidJumps = [];
    // Loop through every step in the stepsArray
    for (const key in stepsArray) {
      // If actions does not exist, skip this step
      if (!stepsArray[key].$actions) continue;
      let jumps = stepsArray[key].$actions.filter((a) => a.action == 'jump_to_step');
      // For each jump in jumps
      for (const i in jumps) {
        let jumpTarget = jumps[i].detail_1;
        // Get the index of the step that is the destination of the current jump
        let targetIndex = stepsArray.findIndex((s) => s.uid == jumpTarget);
        // If the destination does not exist or if the step with the jump is after the destination step, we have an invalid jump
        if (targetIndex < 0 || key > targetIndex) {
          let targetTitle = key > targetIndex ? '[Deleted Step]' : stepsArray[targetIndex].title;
          invalidJumps.push({ source: stepsArray[key].title, trigger: jumps[i].trigger, targetTitle, stepIndex: key, actionIndex: i });
        }
      }
    }
    return invalidJumps;
  }

  stepActionActionChanged(e) {
    // Handler function that clears data from an action if it becomes invalid
    if (e.detail.previousValue && e.detail.value != e.detail.previousValue) {
      this.set(`stepsArray.${e.model.stepIndex}.$actions.${e.model.actionIndex}.detail_1`, null);
      this.set(`stepsArray.${e.model.stepIndex}.$actions.${e.model.actionIndex}.detail_2`, null);
    }
  }

  stepActionAttributeChanged(e) {
    // Handler function that unsets the selected value for a call action
    if (e.detail.previousValue && e.detail.value != e.detail.previousValue) {
      this.set(`stepsArray.${e.model.stepIndex}.$actions.${e.model.actionIndex}.detail_2`, null);
    }
  }

  deleteStepAction(e) {
    this.splice(`stepsArray.${e.currentTarget.stepIndex}.$actions`, e.model.actionIndex, 1);
  }

  getAssessmentActionTriggers() {
    // Default (always included)
    let triggers = { on_finish: 'Completed Assessment' };
    // Special cases for different levels of permission (not bad data, they just wouldn't do anything)
    if (this._itemData?.assessment_permission != 'new_only') triggers.on_select = 'Selected Existing Node';
    if (this._itemData?.assessment_permission != 'existing_only') triggers.on_create = 'Created New Node';

    return triggers;
  }

  getAttributeKeysFilteredByType() {
    let acceptedAttributeTypes = [];
    if (['default', 'existing_only', 'existing_nodes_and_sections', 'new_only'].includes(this._itemData?.assessment_permission))
      acceptedAttributeTypes.push('node');
    if (['existing_sections_only', 'existing_nodes_and_sections'].includes(this._itemData?.assessment_permission))
      acceptedAttributeTypes.push('section');

    let attributeKeys = [];
    for (const attribute in this.otherAttributes) {
      const currentAttributeTypes = this.otherAttributes[attribute].attribute_types;
      if (Array.isArray(currentAttributeTypes) && acceptedAttributeTypes.every((type) => currentAttributeTypes.includes(type))) {
        // Filter down to specific types of attributes
        attributeKeys.push(attribute);
      }
    }
    return attributeKeys;
  }

  addAssessmentAction(e) {
    let model = {
      trigger: null,
      action: 'set_an_attribute',
      detail_1: null,
      detail_2: null,
      overwrite: true
    };
    this.push(`stepsArray`, model);
  }

  assessmentActionAttributeChanged(e) {
    if (e.detail.previousValue && e.detail.value != e.detail.previousValue) {
      this.set(`stepsArray.${this.numAssessmentSteps + e.model.actionIndex}.detail_2`, null);
    }
  }

  deleteAssessmentAction(e) {
    this.splice(`stepsArray`, this.numAssessmentSteps + e.model.actionIndex, 1);
  }

  removeFromToolset(e) {
    delete this._itemData.toolsets[e.model.item.$key];
    this.notifyPath('_itemData.toolsets');
  }

  toolsetSelectedChanged(e) {
    if (e.detail.value) {
      e.currentTarget.value = null;
      if (!this.get('_itemData.toolsets')) this.set('_itemData.toolsets', { [e.detail.value]: true });
      else this.set(`_itemData.toolsets.${e.detail.value}`, true);
    }
  }

  async _saveUpdate(itemData, update, detail) {
    // Save the item first.
    await super._saveUpdate(itemData, update, detail);
    // Update all of the toolsets.
    await FirebaseWorker.ref(`${this.modelSpaceDetails.path}/models/button_groups`).transaction((toolsets) => {
      if (toolsets) {
        for (let key in toolsets) {
          let add = Path.get(itemData, `toolsets.${key}`) === true && toolsets[key][detail.itemKey] == null;
          let remove = Path.get(itemData, `toolsets.${key}`) !== true && toolsets[key][detail.itemKey] != null;
          // Get the toolset being modified.
          let toolset = toolsets[key];
          if (add || remove || detail.keyChanged) {
            // If the tool's key has changed, remove references to it's previous key.
            if (detail.keyChanged) delete toolset[detail.previousKey];
            // If we are removing the item from the toolset, go ahead and do that now.
            if (remove) delete toolset[detail.itemKey];
            // Calc an array of tool keys sorted by priority.
            let sortedKeys = Object.keys(toolset)
              .filter((key) => !key.startsWith('_'))
              .sort((a, b) => toolset[a].priority - toolset[b].priority);
            // Append the new tool to the tool keys array.
            if (add) {
              sortedKeys.push(detail.itemKey);
              toolset[detail.itemKey] = {};
            }
            // Re-cal the priority of the tools.
            sortedKeys.forEach((key, i) => (toolset[key].priority = i));
          }
        }
        return toolsets;
      }
    });
  }

  stepsArrayChanged(changeRecord) {
    let pathParts = changeRecord.path.split('.');
    // Handle array items changing
    let itemIndex = Number(pathParts[1]);
    if (!isNaN(itemIndex)) {
      let item = this.get(`stepsArray.${itemIndex}`);
      let temp = {};
      // If the item is a step, convert data from stepsArray into model structure (side effects in temp)
      if (this.isStep(item)) this.convertToModel(temp, item);
      // Then the item is an assessment action; if the action has all of the required details
      else if (item.trigger && item.detail_1) {
        temp = { trigger: item.trigger, attribute: item.detail_1, value: item.detail_2, overwrite: item.overwrite };
      }
      // Otherwise, return (we don't care about this change yet)
      else return;

      let lazy = new LazySet(this);
      lazy.set(`_itemData.node.attributes.${this.getItemKey(item)}`, temp);
      lazy.notify();

      // Loop through _itemData model and remove steps not present in stepsArray
      let modelItems = this.get('_itemData.node.attributes');
      let stepKeys = this.stepsArray.map((s) => this.getItemKey(s));
      for (let key in modelItems) {
        // If the item is not in stepsArray, delete the item
        if (!stepKeys.includes(key)) this.set(`_itemData.node.attributes.${key}`, null);
      }
    }
    // Handle step deletes: if the changeRecord is of splices and nothing was added in the splice (something was deleted)
    else if (pathParts[1] == 'splices' && Path.get(changeRecord, `value.indexSplices.0.addedCount`) == 0) {
      let itemKey = this.getItemKey(Path.get(changeRecord, `value.indexSplices.0.removed.0`));
      this.set(`_itemData.node.attributes.${itemKey}`, null);
    }
    // Save the state of the display
    this.recordOpenSteps();
  }

  convertToModel(temp, item) {
    // Copy all non-internal (prefix $) properties into temp
    for (let key in item) if (!key.startsWith('$')) temp[key] = item[key];
    // Convert all actions to their proper structure for the model
    for (const key in item.$actions) {
      let action = item.$actions[key];
      // Get the stored key or create a new one
      let pushId = action.key && action.key.length > 3 ? action.key : FirebaseWorker.ref().push().key;
      // If the action has a trigger (without it, the data is invalid)
      if (action.trigger !== null) {
        if (['jump_to_step', 'end_assessment'].includes(action.action)) {
          let destination = action.action == 'end_assessment' ? '$end' : action.detail_1;
          // If the jump should always be taken
          if (action.trigger == '$advance') temp.jump_to = destination;
          // If the jump is conditional upon a certain value
          else Path.set(temp, `jump_to.${pushId}`, { value: action.trigger, destination: destination });
        } else if (action.action == 'set_an_attribute' && action.detail_1) {
          // Set the call, which will either have a trigger of $advance or a picklist item
          Path.set(temp, `calls.${pushId}`, { trigger: action.trigger, attribute: action.detail_1, value: action.detail_2 });
        }
      }
    }
  }

  recordOpenSteps() {
    this.set(`displayCache`, []);
    let steps = this.stepsArray.filter((s) => this.isStep(s));
    for (const key in steps) this.set(`displayCache.${key}`, steps[key].$opened || false);
  }

  assessmentModelChanged(changeRecord) {
    // Ensure only attributes with the correct attribute_types (node or section) are included in the steps
    if (changeRecord.path == '_itemData.assessment_permission') {
      let newStepsArray = structuredClone(this.stepsArray);
      newStepsArray = newStepsArray.filter((step) => {
        if (step.type != 'attribute') return true;
        return this.attributeKeysFilteredByType.includes(step.attribute);
      });
      this.set('stepsArray', newStepsArray);
      this.reorderSteps();
      this.deleteBrokenJumps();
    }
    // If the item has been changed (change record shows only _itemData)
    if (changeRecord.path == '_itemData') {
      let numSteps = 0;
      let newStepsArray = [];
      // Get the 'attributes' step list from the item
      let modelSteps = Object.values(this.get(`_itemData.node.attributes`) || {}).filter((item) => item !== null);
      // For each step in modelSteps
      for (const key in modelSteps) {
        let temp = {};
        // If the model is a step
        if (this.isStep(modelSteps[key])) {
          numSteps += 1;
          // Make a DEEP copy of the step/action
          temp = JSON.parse(JSON.stringify(modelSteps[key]));
          // If the step is type: gps_set_location, update it to set_location
          if (temp.type == 'gps_set_location') temp.type = 'set_location';
          // If the step is an attribute step, set $gui_element
          if (temp.type == 'attribute') temp.$gui_element = this.getAttributeModel(temp.attribute)?.gui_element;
          // Build $actions array
          let $actions = [];
          // If the step has a jump
          if (temp.jump_to) {
            // If the jump is unconditional
            if (typeof temp.jump_to == 'string') {
              $actions.push({
                trigger: '$advance',
                action: temp.jump_to == '$end' ? 'end_assessment' : 'jump_to_step',
                // TODO: This can gets borken wen importing old assessment
                detail_1: temp.jump_to == '$end' ? null : temp.jump_to,
                detail_2: null,
                key: null
              });
            }
            // If there are conditional jump rules
            else {
              for (const i in temp.jump_to) {
                $actions.push({
                  trigger: temp.jump_to[i].value,
                  action: temp.jump_to[i].destination == '$end' ? 'end_assessment' : 'jump_to_step',
                  // TODO: This can gets borken wen importing old assessment
                  detail_1: temp.jump_to[i].destination == '$end' ? null : temp.jump_to[i].destination,
                  detail_2: null,
                  key: i.length > 3 ? i : FirebaseWorker.ref().push().key
                });
              }
            }
            // Delete the jump_to property
            delete temp.jump_to;
          }
          // If the step has calls
          if (temp.calls) {
            for (const i in temp.calls) {
              $actions.push({
                trigger: temp.calls[i].trigger || temp.calls[i].pick_item,
                action: 'set_an_attribute',
                detail_1: temp.calls[i].attribute,
                detail_2: temp.calls[i].value,
                key: i.length > 3 ? i : FirebaseWorker.ref().push().key
              });
            }
            // Delete the calls property
            delete temp.calls;
          }
          // Push the now corrected copy of the step to newStepsArray
          temp.$actions = $actions;
          // Convert old key to new system (backwards compatibility, updating to new system)
          if (this.get(`_itemData.node.attributes.${temp.order + '_' + this.snakeCase(temp.title)}`)) {
            this.set(`_itemData.node.attributes.${this.getItemKey(temp)}`, modelSteps[key]);
            this.set(`_itemData.node.attributes.${temp.order + '_' + this.snakeCase(temp.title)}`, null);
          }
          // If the step doesn't have a uid, add one
          if (!temp.uid) {
            let uid = FirebaseWorker.ref().push().key;
            temp.uid = uid;
            this.set(`_itemData.node.attributes.${this.getItemKey(temp)}.uid`, uid);
          }
        }
        // Otherwise, the model is for an assessment action
        else {
          // Lookup for upgrading previous trigger types to new ones
          if (this.get(`_itemData.node.attributes.x_${this.snakeCase(modelSteps[key].attribute)}`)) {
            this.set(`_itemData.node.attributes.${this.getItemKey(modelSteps[key])}`, modelSteps[key]);
            this.set(`_itemData.node.attributes.x_${this.snakeCase(modelSteps[key].attribute)}`, null);
          }
          temp = {
            trigger: modelSteps[key].trigger || this.oldTriggerTypes[modelSteps[key].type],
            action: 'set_an_attribute',
            detail_1: modelSteps[key].attribute,
            detail_2: modelSteps[key].value,
            overwrite: modelSteps[key].overwrite
          };
        }
        newStepsArray.push(temp);
      }
      // Sort assessment actions to the back, and steps by their order number
      newStepsArray.sort((a, b) => (isNaN(a.order - b.order) ? isNaN(a.order) - isNaN(b.order) : a.order - b.order));
      // If display cache fits the newStepsArray, apply the $opened property appropriately
      if (this.displayCache.length == numSteps) for (const key in this.displayCache) newStepsArray[key].$opened = this.displayCache[key];
      // Populate steps array
      this.set(`stepsArray`, newStepsArray);
    }
  }

  computeDrawingTools() {
    const allToolsEntries = Object.entries(this.tools ?? {});
    const drawingToolsEntries = allToolsEntries.filter(([, tool]) => tool.node?.location === 'new' && tool.view !== 'assessment');
    return drawingToolsEntries.map(([value, { label }]) => ({ value, label }));
  }
}

customElements.define('katapult-assessment-editor', KatapultAssessmentEditor);
